import { Component } from 'preact';
import { ChevronLeft } from 'lucide-react';
import VisibilityStrip from '../play/VisibilityStrip';
import SaveList from '../play/SaveList';
import PublicBrowser from '../play/PublicBrowser';
import TournamentList from '../play/TournamentList';
import { TEXT_PRIMARY, TEXT_MUTED } from '../../lib/medievalTheme';
import { playUI, UI } from '../../utils/arena/uiSounds';
import { k, t } from '../../localization/t';

// Phone layout of the Play screen (SessionBrowser owns the data and
// renders this on isPhoneLayout()). Same structure as desktop: the
// visibility strip on the left, the tabbed session panel on the right.
// The shared vh-* classes are re-specced in rem inside the phone
// canvas (see "Phone re-spec" block in index.css), so the pieces used
// here are the SAME components desktop renders, at native tap sizes.

const TABS = [
  { key: 'saves', label: k('Open Matches') },
  { key: 'public', label: k('Public Games') },
  { key: 'tournaments', label: k('Tournaments') },
];

export default class MobilePlay extends Component {
  handleBack = () => {
    playUI(UI.CLOSE);
    this.props.onBack?.();
  };

  render() {
    const {
      profile, sorceryCards, tab, onTab,
      visibleSaves, visiblePublic, tournaments, loading,
      savedNamesByInstance, onPickVisibility, onRowAction,
      onJoinPublic, onResumeTournament,
    } = this.props;
    const counts = {
      saves: visibleSaves.length,
      public: visiblePublic.length,
      tournaments: tournaments.length,
    };

    return (
      <div className="absolute inset-0 flex flex-col overflow-hidden select-none">
        {/* Same backdrop as desktop SessionBrowser. */}
        <img
          src="/play-bg.webp"
          alt=""
          className="absolute inset-0 w-full h-full object-cover"
          draggable={false}
        />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.45) 40%, rgba(0,0,0,0.5) 70%, rgba(0,0,0,0.85) 100%)' }}
        />

        {/* Top bar: back chip + screen label. Absolute inset-0 spans the
            canvas' padding box, so safe-area insets (notch side in
            landscape) are applied here directly. */}
        <div
          className="relative z-10 flex items-center gap-3 pt-2 pb-1"
          style={{
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
          }}
        >
          <button
            type="button"
            aria-label={t('Back')}
            className="flex items-center justify-center transition-transform duration-100 active:scale-[0.94]"
            style={{
              width: '2.75rem',
              height: '2.75rem',
              borderRadius: 'var(--radius-full)',
              background: 'linear-gradient(180deg, rgba(20,16,10,0.85) 0%, rgba(10,8,5,0.9) 100%)',
              border: '1px solid rgba(180, 140, 60, 0.28)',
              boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
            }}
            onClick={this.handleBack}
          >
            <ChevronLeft size={22} color={TEXT_MUTED} />
          </button>
          <span
            className="arena-heading text-lg font-bold"
            style={{ color: TEXT_PRIMARY, textShadow: '0 1px 2px rgba(0,0,0,0.8)' }}
          >
            {t('Play')}
          </span>
        </div>

        {/* Two columns, like desktop: new-session strip left, browser right. */}
        <div
          className="relative z-10 flex-1 min-h-0 flex gap-3"
          style={{
            paddingLeft: 'max(0.75rem, env(safe-area-inset-left, 0px))',
            paddingRight: 'max(0.75rem, env(safe-area-inset-right, 0px))',
            paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom, 0px))',
          }}
        >
          <div className="overflow-y-auto shrink-0" style={{ width: '15rem' }}>
            <VisibilityStrip onPick={onPickVisibility} />
          </div>

          <div className="vh-forged-panel flex flex-col min-h-0 flex-1">
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                backgroundImage: 'url("/play-bg.webp")',
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                transform: 'scale(1.18)',
                transformOrigin: 'center',
                filter: 'blur(28px) brightness(0.45) saturate(1.1)',
                pointerEvents: 'none',
                zIndex: 0,
              }}
            />
            <div
              aria-hidden
              style={{
                position: 'absolute',
                inset: 0,
                background: 'url("/tex-noise-panel.webp"), linear-gradient(180deg, rgba(14, 11, 8, 0.82) 0%, rgba(8, 6, 4, 0.86) 100%)',
                backgroundBlendMode: 'soft-light, normal',
                pointerEvents: 'none',
                zIndex: 1,
              }}
            />
            <div className="vh-panel-tabs" style={{ position: 'relative', zIndex: 2 }}>
              {TABS.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  className={`vh-panel-tab ${tab === item.key ? 'vh-panel-tab-active' : ''}`}
                  onClick={() => onTab(item.key)}
                >
                  {t(item.label)}
                  {counts[item.key] > 0 ? (
                    <span className="vh-panel-tab-count tabular-nums">{counts[item.key]}</span>
                  ) : null}
                </button>
              ))}
            </div>
            <div
              className="flex-1 min-h-0 overflow-y-auto p-3"
              style={{ position: 'relative', zIndex: 2 }}
            >
              {tab === 'saves' ? (
                <SaveList
                  instances={visibleSaves}
                  loading={loading}
                  currentUserId={profile?.id}
                  sorceryCards={sorceryCards}
                  savedNamesByInstance={savedNamesByInstance}
                  onAction={onRowAction}
                />
              ) : tab === 'tournaments' ? (
                <TournamentList
                  tournaments={tournaments}
                  loading={loading}
                  onResume={onResumeTournament}
                />
              ) : (
                <PublicBrowser
                  instances={visiblePublic}
                  currentUserId={profile?.id}
                  currentUserTier={profile?.rank?.tier}
                  sorceryCards={sorceryCards}
                  onJoin={onJoinPublic}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }
}
